import { type FormEvent, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AuthLayout } from '../components/AuthLayout'
import { GoogleButton } from '../components/GoogleButton'
import { Button, Field, FormError, Toggle } from '../components/ui'
import { ApiError, api, call, messageOf } from '../lib/api'
import { safeNext } from '../lib/auth'
import { inviteQuery, meQuery } from '../lib/queries'

export const Route = createFileRoute('/signup')({
  validateSearch: (s: Record<string, unknown>): { invite?: string; next?: string } => ({
    invite: typeof s.invite === 'string' ? s.invite : undefined,
    next: safeNext(s.next),
  }),
  component: Signup,
})

// Tells Safari's and 1Password's generators what we accept, so a suggested
// password is never one we'd reject. React has no prop for it.
const PASSWORD_RULES = { passwordrules: 'minlength: 10; maxlength: 200;' } as Record<string, string>

function Signup() {
  const { invite, next } = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [f, setF] = useState({ display_name: '', username: '', email: '', password: '' })
  const [reminders, setReminders] = useState(true)
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }))
  const league = useQuery({ ...inviteQuery(invite ?? ''), enabled: !!invite })

  const create = useMutation({
    mutationFn: () => call(api.POST('/auth/signup', { body: { ...f, username: f.username.trim(), invite_code: invite, notify_email: reminders } })),
    onSuccess: (me) => {
      qc.setQueryData(meQuery.queryKey, me)
      navigate({ to: next ?? '/' })
    },
  })
  const fields = create.error instanceof ApiError ? create.error.fields : {}

  const submit = (e: FormEvent) => {
    e.preventDefault()
    create.mutate()
  }

  return (
    <AuthLayout
      title={league.data ? `Join ${league.data.league_name}` : 'Create an account'}
      lede={league.data ? `${league.data.admin_name} invited you. ${league.data.member_count} in so far.` : 'Takes a minute. Then add your cats.'}
      footer={
        <>
          Already have one?{' '}
          <Link to="/login" search={{ next: invite ? `/join/${invite}` : next }} className="font-semibold text-ink underline underline-offset-2">
            Sign in
          </Link>
        </>
      }
    >
      <GoogleButton next={next} invite={invite} label={league.data ? 'Join with Google' : 'Sign up with Google'} appleLabel={league.data ? 'Join with Apple' : 'Sign up with Apple'} />
      <form onSubmit={submit} className="grid gap-4" method="post" action="/api/auth/signup">
        <FormError>{create.isError && messageOf(create.error)}</FormError>
        <Field id="signup-name" label="Your name" name="name" autoComplete="name" required maxLength={60} value={f.display_name} onChange={set('display_name')} error={fields.display_name} help="What the other managers see." />
        <Field id="signup-username" label="Username" name="username" autoComplete="username" autoCapitalize="none" spellCheck={false} required minLength={3} maxLength={32} pattern="[A-Za-z0-9_]+" value={f.username} onChange={set('username')} error={fields.username} help="Letters, numbers and underscores." />
        <Field id="signup-email" label="Email" name="email" type="email" autoComplete="email" required value={f.email} onChange={set('email')} error={fields.email} help="For password resets and round reminders." />
        <Field id="signup-password" label="Password" name="new-password" type="password" autoComplete="new-password" {...PASSWORD_RULES} required minLength={10} maxLength={200} value={f.password} onChange={set('password')} error={fields.password} help="Ten characters or more. A password manager's suggestion is perfect." />
        <Toggle id="signup-reminders" label="Email me round reminders" checked={reminders} onChange={setReminders} help="A nudge when rounds open, close and post. Easy to turn off later." />
        <Button type="submit" variant="primary" block busy={create.isPending}>
          {league.data ? 'Create account and join' : 'Create account'}
        </Button>
        <p className="text-center text-[13px] text-muted">
          By signing up you agree to the{' '}
          <Link to="/terms" className="font-semibold text-ink underline underline-offset-2">Terms</Link>.
        </p>
      </form>
    </AuthLayout>
  )
}
